import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  { id: "home_session", name: "Home Session", description: "A personalised Spinal Flow session in the comfort of your home.", duration: "75 min", price: 180, currency: "usd" },
  { id: "wellness_consult", name: "Wellness Consultation", description: "Holistic 1:1 consultation to design your healing roadmap.", duration: "45 min", price: 90, currency: "usd" },
  { id: "follow_up", name: "Follow-up Care", description: "Continued care between sessions to deepen your practice.", duration: "45 min", price: 110, currency: "usd" },
  { id: "healing_plan", name: "Personalised Healing Plan", description: "Curated multi-session plan tailored to your nervous system.", duration: "4 sessions", price: 640, currency: "usd" },
];

export default function Services({ onBook }) {
  const [services, setServices] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => {
      if (Array.isArray(r.data) && r.data.length) setServices(r.data);
    }).catch(() => {});
  }, []);

  return (
    <section id="services" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 reveal mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-secondary mb-4">Offerings</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight max-w-xl">
              Curated sessions, <span className="italic">held with care.</span>
            </h2>
          </div>
          <p className="max-w-md text-muted-foreground">
            Each offering is shaped around your nervous system today — not yesterday's plan. Choose what calls to you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className="reveal group rounded-3xl bg-card/50 border border-border/60 p-8 md:p-10 flex flex-col justify-between hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10 transition-all duration-500"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-serif text-3xl">{s.name}</h3>
                  <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
                    <Clock size={12} /> {s.duration}
                  </span>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-8">{s.description}</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-serif text-4xl">
                    ${Number(s.price).toFixed(0)}
                    <span className="text-sm text-muted-foreground font-sans ml-1">{(s.currency || 'usd').toUpperCase()}</span>
                  </div>
                </div>
                <Button
                  onClick={() => onBook?.(s.id)}
                  data-testid={`book-now-${s.id}`}
                  className="rounded-full bg-foreground text-background hover:bg-foreground/85 group-hover:translate-x-1 transition-transform"
                >
                  Book Now <ArrowRight size={16} className="ml-1.5" />
                </Button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
